import React from "react";
import { Form as AntForm } from "antd";
import type { FormProps as AntFormProps } from "antd";

import { validators } from "./validators";
import { useApiFormErrors } from "./useApiFormErrors";

export type FormProps<Values = unknown> = AntFormProps<Values>;

/**
 * `Form` — single-import wrapper around antd `Form` that ships with PAM
 * defaults + reusable validators + a server-error mapping hook.
 *
 * **Defaults applied:**
 * - `layout="vertical"` — labels above fields (PAM convention)
 * - `autoComplete="off"` — admin forms shouldn't trigger browser autocomplete
 * - `requiredMark={false}` is set globally via `ConfigProvider`; errors are
 *   shown inline under each field instead of a `*` marker
 *
 * **Statics preserved from antd:**
 * `Form.Item`, `Form.List`, `Form.ErrorList`, `Form.Provider`,
 * `Form.useForm`, `Form.useFormInstance`, `Form.useWatch`.
 *
 * **Custom additions:**
 * - `Form.validators.{matchesField, mustBeTrue, numericRange, noWhitespace, httpUrl}`
 *   — common validation rules; see `validators.ts` for full docs.
 * - `Form.useApiFormErrors(form)` — maps server `{ field, message }[]` to
 *   field-level errors via `form.setFields`.
 *
 * Override any default with a regular prop:
 *
 *   <Form layout="horizontal" autoComplete="on">...</Form>
 */
function FormBase<Values = unknown>(props: FormProps<Values>) {
  // antd `Form` is generic at the value level; JSX with spread + a generic
  // wrapper trips up TypeScript's children inference. `React.createElement`
  // with the typed component cast bypasses this without weakening the
  // public API surface.
  const Component = AntForm as unknown as React.ComponentType<
    FormProps<Values>
  >;
  return React.createElement(Component, {
    layout: "vertical",
    autoComplete: "off",
    ...props,
  });
}

const Form = Object.assign(FormBase, {
  // antd Form statics
  Item: AntForm.Item,
  List: AntForm.List,
  ErrorList: AntForm.ErrorList,
  Provider: AntForm.Provider,
  useForm: AntForm.useForm,
  useFormInstance: AntForm.useFormInstance,
  useWatch: AntForm.useWatch,
  // PAM extensions
  validators,
  useApiFormErrors,
});

export default Form;

// Re-exports for ergonomic named imports.
export {
  matchesField,
  mustBeTrue,
  numericRange,
  noWhitespace,
  httpUrl,
  isHttpUrl,
  validators,
} from "./validators";
export { useApiFormErrors } from "./useApiFormErrors";
export type { ApiFieldError } from "./useApiFormErrors";
